'use client'

import { Check } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { MouseEvent, PropsWithChildren, useEffect, useState } from 'react'

import ControlledWorldSettingDialog from '@/app/[locale]/profile/components/ControlledWorldSettingDialog'
import ConfirmDialog from '@/components/ConfirmDialog'
import EmbedDialog from '@/components/EmbedDialog'
import PopoverMenu from '@/components/PopoverMenu'
import IconCode from '@/components/assets/icon_code.svg'
import IconFB from '@/components/assets/icon_fb.svg'
import IconLink from '@/components/assets/icon_link.svg'
import IconMoreAction from '@/components/assets/icon_more_action.svg'
import IconTwitter from '@/components/assets/icon_twitter.svg'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAppLoaderContext } from '@/context/AppLoaderContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useShareLink } from '@/hooks/useShareLink'
import { showToast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import {
  deleteFeaturedRoom,
  deleteRoom,
  postFeaturedRoom,
  Room,
  withdrawCoOwnedRoom,
} from '@/services/cms'
import Link from 'next/link'

type MoreActionProps = PropsWithChildren<{
  info: Partial<Room>
  shareOnly?: boolean
  isCoOwnedRoom?: boolean
  onAfterMutatingWorld?: (deleteRoomId?: string) => void
  stopSetAsFeatured?: boolean
  isPremiumRoom?: boolean
}>

export default function MoreAction({
  info,
  shareOnly = false,
  isCoOwnedRoom = false,
  children,
  onAfterMutatingWorld,
  stopSetAsFeatured = false,
  isPremiumRoom = false,
}: MoreActionProps) {
  const t = useTranslations()
  const { store } = useConfigsContext()
  const { setIsAppLoading } = useAppLoaderContext()
  const { copyLink, createSharePlatformLink } = useShareLink(info.hubs_url)

  const htcToken = store?.credentials?.htcToken
  const profileId = store?.profile?.id

  const [open, setOpen] = useState(false)
  const [isEmbedDialogOpen, setIsEmbedDialogOpen] = useState(false)
  const [isSettingDialogOpen, setIsSettingDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isWithdrawDialogOpen, setIsWithdrawDialogOpen] = useState(false)

  const isDisabledSetAsFeatured = stopSetAsFeatured && !info.is_featured

  const handlePopoverOpenChange = async () => {
    setOpen((open) => !open)
  }

  const handleCopyLink = () => {
    copyLink()
    setOpen(false)
  }

  const handleClickIcon = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.nativeEvent.preventDefault()
  }

  const handleDeleteRoom = async () => {
    if (!htcToken) throw new Error('missing htcToken')
    if (!info.id) throw new Error('missing room id')

    setIsAppLoading(true)

    const res = await deleteRoom(htcToken, info.id)

    if (res?.status === 200) {
      setOpen(false)
      onAfterMutatingWorld?.(info.id)
    }
  }

  const handleWithDrawCoOwnedRoom = async () => {
    if (!htcToken) throw new Error('missing htcToken')
    if (!info.id) throw new Error('missing room id')
    if (!profileId) throw new Error('missing profile id')

    setIsAppLoading(true)

    const res = await withdrawCoOwnedRoom(htcToken, info.id, profileId)

    if (res?.status === 200) {
      setOpen(false)
      onAfterMutatingWorld?.()
    }
  }

  const handleSetAsFeatured = async () => {
    if (!htcToken) throw new Error('missing htcToken')
    if (!info.id) throw new Error('missing room id')

    setIsAppLoading(true)

    const res = await postFeaturedRoom(htcToken, info.id)

    if (res?.status === 200) {
      showToast({
        message: t('profile-page.set-as-featured-success'),
        type: 'success',
      })
      setOpen(false)
      onAfterMutatingWorld?.(info.id)
    }
  }

  const handleRemoveFeatured = async () => {
    if (!htcToken) throw new Error('missing htcToken')
    if (!info.id) throw new Error('missing room id')

    setIsAppLoading(true)

    const res = await deleteFeaturedRoom(htcToken, info.id)

    if (res?.status === 200) {
      showToast({
        message: t('profile-page.remove-featured-success'),
        type: 'success',
      })
      setOpen(false)
      onAfterMutatingWorld?.(info.id)
    }
  }

  const handleOpenWorldSettingDialog = () => {
    setIsSettingDialogOpen(true)
    setOpen(false)
  }

  useEffect(() => {
    if (!isSettingDialogOpen) return

    const handleChangeRoute = () => {
      setIsSettingDialogOpen(false)
    }

    document.addEventListener('onChangeRoute', handleChangeRoute as EventListener)
    return () => {
      document.removeEventListener('onChangeRoute', handleChangeRoute as EventListener)
    }
  }, [isSettingDialogOpen])

  return (
    <>
      <Popover open={open} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild onClick={handleClickIcon}>
          <div className="subheader-2 1.5xl:subheader-1 1.5xl:size-6 hover:[&_path]:fill-primary size-5">
            <IconMoreAction className="fill-vive-primary-20" />
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="border-none bg-transparent p-0">
          {children ||
            (info.hubs_url && (
              <PopoverMenu className="rounded-[3px]">
                <PopoverMenu.Group title={t('profile-page.share')}>
                  <Link href={createSharePlatformLink('twitter', info.title)} target="_blank">
                    <PopoverMenu.Item>
                      <IconTwitter className="size-4 fill-[#D5E2F2]" />
                      <div className="body-1 text-inherit">{t('profile-page.share-twitter')}</div>
                    </PopoverMenu.Item>
                  </Link>
                  <Link href={createSharePlatformLink('facebook')} target="_blank">
                    <PopoverMenu.Item>
                      <IconFB className="size-4 fill-[#D5E2F2]" />
                      <div className="body-1 text-inherit">{t('profile-page.share-facebook')}</div>
                    </PopoverMenu.Item>
                  </Link>
                  <PopoverMenu.Item onClick={handleCopyLink}>
                    <IconLink className="size-4 fill-[#D5E2F2]" />
                    <div className="body-1 text-inherit">{t('profile-page.share-copy-link')}</div>
                  </PopoverMenu.Item>
                  {!info.is_standalone && (
                    <PopoverMenu.Item className="group">
                      <div
                        className="flex w-full items-center gap-2"
                        onClick={() => setIsEmbedDialogOpen(true)}
                      >
                        <IconCode className="size-4 fill-[#D5E2F2]" />
                        <div className="body-1 transition-all group-hover:text-white">
                          {t('embed-modal.title')}
                        </div>
                      </div>
                    </PopoverMenu.Item>
                  )}
                </PopoverMenu.Group>
                {!shareOnly && (
                  <>
                    <PopoverMenu.Separator />
                    {info.policy === 'allow_any_user' && (
                      <PopoverMenu.Item
                        disabled={isDisabledSetAsFeatured}
                        onClick={info.is_featured ? handleRemoveFeatured : handleSetAsFeatured}
                        className={cn({
                          'text-vive-primary-40': isDisabledSetAsFeatured,
                        })}
                      >
                        {info.is_featured ? (
                          <div className="text-vive-brand-50 flex w-full items-center justify-between">
                            {t('user-worlds.sort-option.featured')}
                            <Check className="stroke-vive-brand-50 size-4" />
                          </div>
                        ) : (
                          t('profile-page.my-worlds-card-set-as-featured')
                        )}
                      </PopoverMenu.Item>
                    )}
                    <PopoverMenu.Item>
                      <div
                        className="body-1 w-full text-left text-inherit"
                        onClick={() => handleOpenWorldSettingDialog()}
                      >
                        {t('profile-page.my-worlds-card-settings')}
                      </div>
                    </PopoverMenu.Item>
                    {isCoOwnedRoom && (
                      <PopoverMenu.Item>
                        <div
                          className="body-1 w-full text-left text-inherit"
                          onClick={() => setIsWithdrawDialogOpen(true)}
                        >
                          {t('profile-page.co-owned-room-withdraw-cta')}
                        </div>
                      </PopoverMenu.Item>
                    )}
                    {!isCoOwnedRoom && !isPremiumRoom && (
                      <PopoverMenu.Item>
                        <div
                          className="body-1 w-full text-left text-inherit"
                          onClick={() => setIsDeleteDialogOpen(true)}
                        >
                          {t('profile-page.my-worlds-card-delete')}
                        </div>
                      </PopoverMenu.Item>
                    )}
                  </>
                )}
              </PopoverMenu>
            ))}
        </PopoverContent>
      </Popover>
      <EmbedDialog
        title={t('embed-modal.title')}
        info={info}
        isDialogOpen={isEmbedDialogOpen}
        handleOpenChange={setIsEmbedDialogOpen}
        handleClose={() => setIsEmbedDialogOpen(false)}
      />
      <ControlledWorldSettingDialog
        info={info}
        open={isSettingDialogOpen}
        onOpenChange={setIsSettingDialogOpen}
        onAfterMutatingWorld={onAfterMutatingWorld}
      />
      <ConfirmDialog
        title={t('profile-page.co-owned-room-withdraw-cta')}
        content={t('profile-page.co-owned-room-withdraw-caption')}
        onConfirm={handleWithDrawCoOwnedRoom}
        open={isWithdrawDialogOpen}
        onOpenChange={setIsWithdrawDialogOpen}
      />
      <ConfirmDialog
        title={t('profile-page.my-worlds-card-delete')}
        content={t('profile-page.my-worlds-card-delete-caption')}
        onConfirm={handleDeleteRoom}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      />
    </>
  )
}
